import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, FileText, X, CheckCircle2, ChevronLeft, ChevronRight, AlertTriangle, Loader2 } from "lucide-react";
import { message } from "antd";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";

import { useAnalyzePlanSubmittalMutation } from "graphql/mutations/analyze-plan-submittal.generated";
import { useGetPlanReviewForResubmissionQuery } from "graphql/queries/plan-review-for-resubmission.generated";
import { useCreatePlanReviewCheckoutMutation } from "graphql/mutations/create-plan-review-checkout.generated";

import useFileDropzone from "hooks/useFileDropzone";
import { useFileUpload } from "hooks/useFileUpload";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { UploadScope } from "state/reactive-vars/uploading-files-var";
import { usePlanReviewFlowStore, PlanReviewStagedDocument } from "state/zustand-stores/plan-review-flow-store";
import routeNames from "constants/route-names";
import { PLAN_REVIEW_LARGE_FILE_WARNING_BYTES, PLAN_REVIEW_MAX_FILE_SIZE_BYTES } from "constants/constants";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";

type StagedDoc = PlanReviewStagedDocument;

// Infer the document category from the file name so the report can label it
// (Plans / Calculations / Cut Sheets / …) without asking the user, matching the design.
//
// The drawing checks run first because a plan set is often named "… Permit Set.pdf",
// which would otherwise be classified as an application form.
const inferDocType = (name: string): string => {
	const n = name.toLowerCase();
	if (n.includes("plan") || n.includes("drawing") || n.includes("permit set")) return "plans";
	if (n.includes("calc")) return "calculations";
	if (n.includes("cut") || n.includes("spec") || n.includes("sheet")) return "cut_sheets";
	if (n.includes("application") || n.includes("form") || n.includes("permit")) return "application_form";
	if (n.includes("cover") || n.includes("letter")) return "cover_letter";
	return "other";
};

const formatBytes = (bytes: number): string => {
	if (!bytes) return "";
	const units = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

/**
 * "Select and Upload Your files" step. The AHJ user uploads the whole plan package and submits
 * it for review. Unlike Ollie (item-by-item), the package is analyzed all at once:
 * "Start Review" calls a SINGLE action (analyze_plan_submittal) that creates the
 * project and its first submission, stores every document, kicks off the analysis, and
 * then we navigate to
 * the report which shows analyzing → complete → report.
 *
 * With a :planReviewId in the URL the screen adds another submission to an existing
 * project. A revision is its own package: only the files uploaded here are part of it,
 * and the documents of the submittals already reviewed stay on them — they are the
 * package being revised, not the revision. Submitting creates a NEW job; nothing from
 * the previous submissions is touched.
 *
 * The exception is a submittal whose analysis failed: it was never reviewed, so the
 * screen that sends the user back here pre-stages its package for them to fix and send
 * again.
 */
const PlanReviewCollection = () => {
	const navigate = useNavigate();
	// Present when the user is adding a submission to an existing project. The project
	// is then the source of truth for the package context, so the screen still works on
	// a refresh or a direct link, when the store is empty.
	const { planReviewId } = useParams<{ planReviewId?: string }>();
	const selection = usePlanReviewFlowStore(state => state.selection);
	const setSelection = usePlanReviewFlowStore(state => state.setSelection);
	const clearSelection = usePlanReviewFlowStore(state => state.clearSelection);
	const { loggedUserValue } = useLoggedUser();
	const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

	const { uploadFiles, uploadingFiles, removeFile, clearAllFiles } = useFileUpload(UploadScope.PLAN_REVIEW);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Empty on a revision; pre-staged only when the user came back to fix a submittal
	// whose analysis failed.
	const [stagedDocs, setStagedDocs] = useState<StagedDoc[]>(() => selection?.documents ?? []);
	const [isUploading, setIsUploading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	// Set once the package is submitted, so clearing the selection afterwards does
	// not trip the "no selection" guard below and bounce the user back to setup.
	const submittedRef = useRef(false);
	// The project query re-emits (cache, then network), and the context is read from it
	// only once so a later emission cannot overwrite it mid-flow.
	const hydratedFromJobRef = useRef(false);

	const { data: planReviewData, loading: loadingJob } = useGetPlanReviewForResubmissionQuery({
		skip: !planReviewId,
		variables: { id: planReviewId as string },
		fetchPolicy: "cache-and-network"
	});
	const planReview = planReviewData?.plan_reviews_by_pk;
	// Submissions come back newest first, so this is the one this package follows.
	const latestSubmission = planReview?.plan_review_jobs?.[0];
	// Tells Ollie what the reader is putting together, so "what do I need to include?" lands.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen: planReviewId
				? "The upload screen for a revised submittal on an existing plan review project. The reader is choosing the documents to re-submit."
				: "The upload screen for a new plan review submittal. The reader is choosing the property, the jurisdiction, the submission type and the documents to file.",
			ids: [
				["plan_reviews.id", planReviewId],
				["plan_review_jobs.id (the submittal being revised)", latestSubmission?.id]
			],
			propertyId: planReview?.property_id,
			jurisdictionId: planReview?.jurisdiction_id,
			organizationId: planReview?.organization_id
		}),
		{ propertyId: planReview?.property_id, jurisdictionId: planReview?.jurisdiction_id }
	);

	const nextSubmissionNumber = Number(latestSubmission?.submission_number ?? 0) + 1;

	// Read the context off the project the URL points at, so the screen still works on
	// a refresh or a direct link, when the store is empty.
	useEffect(() => {
		if (!planReview || hydratedFromJobRef.current) return;
		hydratedFromJobRef.current = true;
		setSelection({
			organizationId: planReview.organization_id ?? "",
			propertyId: planReview.property_id ?? "",
			jurisdictionId: planReview.jurisdiction_id ?? "",
			submissionTypeId: planReview.submission_type_id ?? "",
			propertyName: planReview.property?.name ?? ""
		});
	}, [planReview, setSelection]);

	// If the user reached this step without a selection (e.g. a direct link or a
	// refresh that dropped the stored selection), send them back to the setup screen.
	// With a project id in the URL the context is fetched above, so wait for it instead.
	useEffect(() => {
		if (submittedRef.current) return;
		if (planReviewId) return;
		// A staged property counts as a valid target even though it has no id yet.
		if (!selection?.propertyId && !selection?.newProperty) {
			navigate(routeNames.PLAN_REVIEW_ADD, { replace: true });
		}
	}, [planReviewId, selection, navigate]);

	// Start each package from a clean upload slot, and leave it clean on the way out,
	// so a file staged for a previous package never blocks this one as a "duplicate".
	useEffect(() => {
		clearAllFiles();
		return () => clearAllFiles();
	}, []);

	const [analyzePlanSubmittal] = useAnalyzePlanSubmittalMutation();
	const [createPlanReviewCheckout] = useCreatePlanReviewCheckoutMutation();
	// Set when analyze_plan_submittal refuses a resubmission: either the project's
	// initial fee was never paid (cycleNumber null, price unknown here — the report
	// screen computes and charges it), or an extra resubmittal past the schedule's
	// included cycles has to be bought first (cycleNumber + price both set).
	const [resubmittalPaymentRequired, setResubmittalPaymentRequired] = useState<{
		cycleNumber: number | null;
		price: number | null;
	} | null>(null);
	const [isRedirectingToCheckout, setIsRedirectingToCheckout] = useState(false);

	const handleFilesSelected = useCallback(
		async (fileList: FileList | null) => {
			if (!fileList || fileList.length === 0) return;

			// Only the upload ceiling is enforced here; the backend prepares whatever is
			// under it for the model.
			const files = Array.from(fileList);
			const oversized = files.filter(f => f.size > PLAN_REVIEW_MAX_FILE_SIZE_BYTES);
			oversized.forEach(f =>
				message.error(
					`${f.name} is ${formatBytes(f.size)}. Files must be under ${formatBytes(
						PLAN_REVIEW_MAX_FILE_SIZE_BYTES
					)} — please split the document and upload the parts separately.`
				)
			);
			const acceptedFiles = files.filter(f => f.size <= PLAN_REVIEW_MAX_FILE_SIZE_BYTES);
			if (acceptedFiles.length === 0) {
				if (fileInputRef.current) fileInputRef.current.value = "";
				return;
			}

			const sizeByName = new Map(acceptedFiles.map(f => [f.name, f.size]));
			setIsUploading(true);
			try {
				const results = await uploadFiles(acceptedFiles);
				const newDocs: StagedDoc[] = results
					.filter((r): r is NonNullable<typeof r> => !!r?.publicUrl)
					.map(r => {
						const name = r.fileName ?? "document";
						return {
							localId: r.id,
							name,
							mimeType: r.mimeType ?? "application/pdf",
							size: sizeByName.get(name) ?? 0,
							publicUrl: r.publicUrl as string,
							docType: inferDocType(name)
						};
					});
				// A pre-staged document (the package of a failed submittal) is not in the
				// upload slot, so drop any re-upload of the same file here instead.
				setStagedDocs(prev => [
					...prev.filter(d => !newDocs.some(n => n.name === d.name && n.size === d.size)),
					...newDocs
				]);
			} catch (error) {
				console.error("Plan review upload failed:", error);
				message.error("Some files couldn't be uploaded. Please try again.");
			} finally {
				setIsUploading(false);
				if (fileInputRef.current) fileInputRef.current.value = "";
			}
		},
		[uploadFiles]
	);

	const { isDraggingOver, dropzoneProps } = useFileDropzone(handleFilesSelected, isUploading);

	// Adding a submission starts from the project, not from the setup screen, so Back
	// returns to the project the submission belongs to.
	const handleBack = useCallback(() => {
		navigate(
			planReviewId
				? routeNames.PLAN_REVIEW_SUBMISSIONS.replace(":planReviewId", planReviewId)
				: routeNames.PLAN_REVIEW_ADD
		);
	}, [planReviewId, navigate]);

	const handleRemove = useCallback(
		(localId: string) => {
			setStagedDocs(prev => prev.filter(d => d.localId !== localId));
			// Also drop it from the upload slot, otherwise re-adding the same file is
			// rejected as a duplicate even though it is no longer in the package.
			removeFile(localId);
		},
		[removeFile]
	);

	// The file list has to show a row the instant a file is picked, not once the whole
	// batch finishes: stagedDocs only gains an entry once uploadFiles resolves, but the
	// upload slot (uploadingFiles) updates per file as each one progresses. A file that
	// errors already got its toast from the hook, so it is left out here — same as
	// before, it just never joins the package.
	const fileRows = useMemo(() => {
		const stagedIds = new Set(stagedDocs.map(d => d.localId));
		const pending = uploadingFiles.filter(f => !stagedIds.has(f.id) && f.status !== "error");
		return [
			...stagedDocs.map(d => ({
				key: d.localId,
				name: d.name,
				size: d.size,
				href: d.publicUrl as string | undefined,
				isUploading: false,
				onRemove: () => handleRemove(d.localId)
			})),
			...pending.map(f => ({
				key: f.id,
				name: f.fileName ?? f.file.name,
				size: f.file.size,
				href: undefined,
				isUploading: true,
				onRemove: () => removeFile(f.id)
			}))
		];
	}, [stagedDocs, uploadingFiles, handleRemove, removeFile]);

	const handleAnalyze = useCallback(async () => {
		// A package targets either an existing property or one staged for creation.
		if (stagedDocs.length === 0 || !(selection?.propertyId || selection?.newProperty)) return;
		setIsSubmitting(true);
		setResubmittalPaymentRequired(null);
		try {
			const result = await analyzePlanSubmittal({
				variables: {
					input: {
						// Resubmitting: the backend adds a submission to this project instead
						// of starting a new one. The previous submissions are kept.
						plan_review_id: planReviewId ?? null,
						property_id: selection.propertyId || null,
						jurisdiction_id: selection.jurisdictionId || null,
						review_type: "fire_plan_review",
						// Ignored when resubmitting: the project keeps the type it started with.
						submission_type_id: selection.submissionTypeId || null,
						created_by_user_id: loggedUserValue?.id,
						base_url: baseUrl,
						workspace_id: loggedUserValue?.workspace_id,
						organization_id: selection.organizationId || null,
						// Created by the backend in this same transaction when the user added
						// them inside the flow; null when they picked existing records.
						new_organization: selection.newOrganization ?? null,
						new_property: selection.newProperty ?? null,
						new_user: selection.newUser ?? null,
						// Ignored by the backend on a resubmission — the project already has its
						// own values from when it was first created.
						fee_variable_inputs: selection.feeVariableInputs ?? null,
						documents: stagedDocs.map(d => ({
							uploaded_by_user_id: loggedUserValue?.id,
							doc_type: d.docType,
							file_name: d.name,
							file_url: d.publicUrl,
							mime_type: d.mimeType,
							file_size_bytes: d.size || null
						}))
					}
				}
			});

			const out = result.data?.analyze_plan_submittal;
			if (out?.payment_required) {
				setResubmittalPaymentRequired({
					cycleNumber: out.cycle_number ?? null,
					price: out.extra_resubmittal_price ?? null
				});
				setIsSubmitting(false);
				return;
			}
			if (!out?.success || !out?.job_id) {
				throw new Error(out?.error || "Could not submit the package.");
			}

			// Mark as submitted BEFORE clearing the selection so the guard effect does
			// not redirect to setup. The project now owns the context, so drop the
			// selection.
			submittedRef.current = true;
			clearSelection();
			clearAllFiles();
			navigate(
				routeNames.PLAN_REVIEW_DETAILS.replace(":planReviewId", out.plan_review_id ?? (planReviewId as string)).replace(
					":jobId",
					out.job_id
				)
			);
		} catch (error) {
			console.error("Analyze plan submittal failed:", error);
			message.error("We couldn't submit the package for review. Please try again.");
			setIsSubmitting(false);
		}
	}, [
		planReviewId,
		stagedDocs,
		selection,
		loggedUserValue,
		baseUrl,
		analyzePlanSubmittal,
		clearSelection,
		clearAllFiles,
		navigate
	]);

	// Redirects to Stripe Checkout for the extra resubmittal fee. Coming back to this
	// same upload URL after paying lets the applicant submit the package that triggered
	// the payment requirement without having to redo anything.
	const handleBuyResubmittal = useCallback(async () => {
		if (!resubmittalPaymentRequired || !planReviewId) return;
		setIsRedirectingToCheckout(true);
		try {
			const returnUrl = window.location.href;
			const result = await createPlanReviewCheckout({
				variables: {
					input: {
						plan_review_id: planReviewId,
						cycle_number: resubmittalPaymentRequired.cycleNumber,
						success_url: returnUrl,
						cancel_url: returnUrl
					}
				}
			});
			const checkoutUrl = result.data?.create_plan_review_checkout?.checkout_url;
			if (!checkoutUrl) throw new Error("Could not start checkout.");
			window.location.href = checkoutUrl;
		} catch (error) {
			console.error("Create plan review checkout failed:", error);
			message.error("We couldn't start checkout for the extra resubmittal. Please try again.");
			setIsRedirectingToCheckout(false);
		}
	}, [resubmittalPaymentRequired, planReviewId, createPlanReviewCheckout]);

	// Documents carried over from a previous submission may predate the size limit, so the
	// package is only submittable once every file is within it.
	const oversizedDocs = useMemo(() => stagedDocs.filter(d => d.size > PLAN_REVIEW_MAX_FILE_SIZE_BYTES), [stagedDocs]);

	// Warned about up front, so the extra minutes of preparation are not read as a
	// progress bar that is stuck.
	const largeDocs = useMemo(() => stagedDocs.filter(d => d.size > PLAN_REVIEW_LARGE_FILE_WARNING_BYTES), [stagedDocs]);

	const canAnalyze = stagedDocs.length > 0 && oversizedDocs.length === 0 && !isUploading && !isSubmitting;

	return (
		<>
			<OllieLayout>
				<div className="psf-page">
					<div className="psf-shell">
						<div className="psf-card">
							<h1 className="psf-upload-title">{planReviewId ? "Submit Revisions" : "Select and Upload Your files"}</h1>

							{planReview && (
								<p className="psf-resubmit-note">
									<span className="psf-resubmit-badge">Submittal #{nextSubmissionNumber}</span>
									<span>Upload your revisions for {planReview.property?.name ?? "this property"}.</span>
								</p>
							)}

							<label
								className={`psf-dropzone ${isUploading ? "psf-dropzone-busy" : ""} ${
									isDraggingOver ? "psf-dropzone-active" : ""
								}`}
								{...dropzoneProps}
							>
								<input
									ref={fileInputRef}
									type="file"
									multiple
									accept=".pdf,application/pdf,image/*,.doc,.docx"
									className="psf-dropzone-input"
									onChange={e => handleFilesSelected(e.target.files)}
								/>
								<Upload className="psf-dropzone-icon" />
								<span className="psf-dropzone-title">
									{isUploading
										? "Uploading…"
										: isDraggingOver
											? "Drop the files here"
											: "Drag and drop or tap to upload"}
								</span>
								<span className="psf-dropzone-hint">
									PDFs only · up to {formatBytes(PLAN_REVIEW_MAX_FILE_SIZE_BYTES)} per file
								</span>
							</label>

							{oversizedDocs.length > 0 && (
								<div className="psf-file-warning">
									<AlertTriangle size={16} />
									<span>
										{oversizedDocs.map(d => d.name).join(", ")} {oversizedDocs.length === 1 ? "exceeds" : "exceed"} the{" "}
										{formatBytes(PLAN_REVIEW_MAX_FILE_SIZE_BYTES)} limit. Remove them (or upload smaller parts) to
										submit the package.
									</span>
								</div>
							)}

							{oversizedDocs.length === 0 && largeDocs.length > 0 && (
								<div className="psf-file-warning">
									<AlertTriangle size={16} />
									<span>
										{largeDocs.length === 1 ? "This file is" : "These files are"} large, so we optimize{" "}
										{largeDocs.length === 1 ? "it" : "them"} for review before analyzing. Expect the review to take a
										few extra minutes.
									</span>
								</div>
							)}

							{fileRows.length > 0 && (
								<div className="psf-file-list">
									{fileRows.map(row => (
										<div key={row.key} className="psf-file-row">
											<div className="psf-file-icon">
												<FileText size={20} />
											</div>
											<div className="psf-file-info">
												{row.href ? (
													<a
														href={row.href}
														target="_blank"
														rel="noreferrer"
														className="psf-file-name"
														title={row.name}
													>
														{row.name}
													</a>
												) : (
													<span className="psf-file-name" title={row.name}>
														{row.name}
													</span>
												)}
												{row.size ? <span className="psf-file-size">{formatBytes(row.size)}</span> : null}
											</div>
											{row.isUploading ? (
												<Loader2 className="psf-file-spinner" size={20} />
											) : (
												<CheckCircle2 className="psf-file-check" size={20} />
											)}
											<button type="button" className="psf-file-remove" onClick={row.onRemove}>
												<X size={18} />
											</button>
										</div>
									))}
								</div>
							)}

							{resubmittalPaymentRequired && (
								<div className="psf-file-warning">
									<AlertTriangle size={16} />
									<span>
										{resubmittalPaymentRequired.price != null
											? `This project has used its included resubmittals. Buy this resubmittal for $${resubmittalPaymentRequired.price.toFixed(2)} to submit these revisions.`
											: "Pay the Ollie pre-screening fee for this project to submit revisions."}
									</span>
								</div>
							)}

							<div className="psf-upload-actions">
								<button className="psf-back-btn" onClick={handleBack}>
									<ChevronLeft size={16} />
									<span>Back</span>
								</button>
								{resubmittalPaymentRequired ? (
									<button
										className={`psf-analyze-btn ${isRedirectingToCheckout ? "psf-analyze-btn-disabled" : ""}`}
										disabled={isRedirectingToCheckout}
										onClick={handleBuyResubmittal}
									>
										<span>
											{isRedirectingToCheckout
												? "Redirecting…"
												: resubmittalPaymentRequired.price != null
													? `Buy resubmittal for $${resubmittalPaymentRequired.price.toFixed(2)}`
													: "Pay to continue"}
										</span>
										<ChevronRight size={16} />
									</button>
								) : (
									<button
										className={`psf-analyze-btn ${!canAnalyze ? "psf-analyze-btn-disabled" : ""}`}
										disabled={!canAnalyze}
										onClick={handleAnalyze}
									>
										<span>{planReviewId ? "Submit" : "Start Review"}</span>
										<ChevronRight size={16} />
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			</OllieLayout>

			<LoadingOverlay
				isLoading={isSubmitting || (!!planReviewId && loadingJob && !planReview)}
				tip={isSubmitting ? "Submitting package…" : ""}
			/>
		</>
	);
};

export default PlanReviewCollection;
